import { useId, type ReactNode } from "react";
import { IconTile } from "@/components/ui/IconTile";
import { Panel } from "@/components/ui/Panel";

/**
 * One numbered step of a page: a card whose header has the step's number in a
 * filled circle, its title, and the line that says what the step is for, over
 * what the step holds.
 *
 * The card is a region named by its title and described by its intro, so a
 * reader can move between the steps of a page by landmark. The number is the
 * page's order, which the reader already has, so it is hidden from them. A card
 * the page does not count, as the Novelty Analysis list, has no number.
 */
export function StepCard({
  number,
  title,
  intro,
  beside,
  aside,
  testId,
  children,
}: {
  number?: number;
  title: string;
  intro: ReactNode;
  /** Beside the title: a help prompt, or a count of what the step holds. */
  beside?: ReactNode;
  /** At the header's far end: a prompt about the whole step, as a key. */
  aside?: ReactNode;
  testId?: string;
  children?: ReactNode;
}) {
  const id = useId();
  return (
    <Panel
      as="section"
      aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-intro`}
      data-testid={testId}
      className="mt-4"
    >
      <div className="mb-3 flex items-center gap-3">
        {number !== undefined && (
          <IconTile tone="solid" shape="round" numeral={number} />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="flex flex-wrap items-center gap-2">
            <h2
              id={`${id}-title`}
              className="text-base font-extrabold tracking-tight"
            >
              {title}
            </h2>
            {beside}
          </span>
          <p id={`${id}-intro`} className="text-xs text-subtle-foreground">
            {intro}
          </p>
        </div>
        {aside}
      </div>
      {children}
    </Panel>
  );
}
